/**
 * Top app bar.
 *
 * Two layouts share one <header>:
 *   - Phone (< sm): an amber brand bar — menu (left), "keep" wordmark centered,
 *     settings (right). Search lives in the bottom MobileToolbar, so it's absent
 *     here. Theme lives in Settings → Appearance, so there's no toggle here.
 *   - Desktop (≥ sm): the normal elevated bar — logo + wordmark (left), search
 *     box, settings. The menu button still shows below lg (no persistent sidebar).
 *
 * The bar is sticky to the top of the scroll container and carries
 * safe-area-inset-top padding so it sits correctly under the iOS notch.
 */

import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { Menu, Search, Settings, X } from "lucide-react";

interface HeaderProps {
  onOpenDrawer: () => void;
  search: string;
  onSearch: (q: string) => void;
}

export function Header({ onOpenDrawer, search, onSearch }: HeaderProps) {
  const router = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  return (
    <header
      className="app-chrome sticky top-0 z-30 border-b bg-amber-400 border-amber-500/60 sm:bg-[var(--bg-elevated)] sm:border-[color:var(--border)]"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {/* Phone: amber brand bar with the wordmark centered. */}
      <div className="flex sm:hidden items-center relative px-2 py-2 text-black">
        <button
          onClick={onOpenDrawer}
          aria-label="Open menu"
          title="Open menu"
          className="p-2 rounded hover:bg-black/10"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link
          to="/"
          aria-label="Home"
          className="absolute left-1/2 -translate-x-1/2 font-extrabold tracking-tight text-lg leading-none"
        >
          keep
        </Link>
        <Link
          to="/settings"
          aria-label="Settings"
          title="Settings"
          className="ml-auto p-2 rounded hover:bg-black/10"
        >
          <Settings className="w-5 h-5" />
        </Link>
      </div>

      {/* Desktop: logo + search + settings. */}
      <div className="hidden sm:flex items-center gap-2 px-3 py-2" style={{ color: "var(--fg)" }}>
        <button
          onClick={onOpenDrawer}
          aria-label="Open menu"
          title="Open menu"
          className="lg:hidden p-2 rounded hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Link to="/" className="flex items-center gap-2 font-bold tracking-tight" aria-label="Home">
          <span
            aria-hidden
            className="inline-flex w-6 h-6 rounded bg-amber-400 text-black items-center justify-center text-xs font-black leading-none"
          >
            k
          </span>
          <span>keep</span>
        </Link>

        <div className="flex-1 flex items-center justify-end gap-2">
          <div className="flex items-center gap-2 flex-1 max-w-xl">
            <div
              className="flex items-center gap-2 px-2 py-1.5 rounded border w-full"
              style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            >
              <Search className="w-4 h-4" style={{ color: "var(--fg-muted)" }} />
              <input
                type="search"
                placeholder="Search notes…"
                value={search}
                onFocus={() => {
                  if (router !== "/search") navigate({ to: "/search" });
                }}
                onChange={(e) => onSearch(e.target.value)}
                className="bg-transparent outline-none flex-1 text-sm"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => onSearch("")}
                  aria-label="Clear search"
                  title="Clear search"
                  className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <Link
            to="/settings"
            aria-label="Settings"
            title="Settings"
            className="p-2 rounded hover:bg-black/5 dark:hover:bg-white/5"
          >
            <Settings className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </header>
  );
}
